import { c as _c } from "react/compiler-runtime";
import { Map, MapControls, MapMarker, MarkerContent, useMap } from "@repo/ui/components/map";
import * as maplibregl from "maplibre-gl";
import { useEffect, useMemo, useRef } from "react";
import { haversineDistance } from "@/lib/utils/exif";

/**
 * Unified admin-review map showing a place + its features with a single
 * consistent visual language:
 *
 * - **Place** — blue pin, always rendered, labeled "Plats" on hover.
 * - **Feature** — amber (pending) / green (approved) circle, labeled with the
 *   feature type name on hover.
 * - **Exactly one** marker is draggable per view. Non-draggable markers give
 *   context only. The draggable one has a dashed ring and `cursor: grab`.
 * - A feature with `unplaced: true` is rendered with a dashed outline to make
 *   the "no saved GPS position" state visible.
 * - **Movement visualisation**: when a ghost marker + its matching live
 *   draggable marker are present, the map draws a dashed slate line between
 *   the two plus a distance pill at the midpoint. The ghost is rendered as a
 *   hollow outline (not a faded solid) so the "original position" is
 *   visually distinct from a regular marker.
 *
 * Callbacks fire on `dragend`; the caller decides when to actually persist.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CARTO_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
const MOVEMENT_LINE_SOURCE = "review-movement-line";
const MOVEMENT_LINE_LAYER = "review-movement-line-layer";

/** Below this (meters) the line + ghost + pill all collapse visually, so we
 *  just hide them — the admin didn't really move anything. */
const MOVEMENT_MIN_METERS = 2;
const PLACE_COLOR = "#2563eb"; // blue-600

const FEATURE_COLORS = {
  draft: "#f59e0b",
  pending: "#f59e0b",
  under_review: "#3b82f6",
  approved: "#22c55e",
  rejected: "#9ca3af"
};
function formatDistance(m) {
  if (m < 10) return `${m.toFixed(1)} m`;
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}
function findMovementPair(markers) {
  const ghost = markers.find(m => m.ghost);
  const live = markers.find(m => m.draggable && !m.ghost);
  if (!ghost || !live) return null;
  if (ghost.kind !== live.kind) return null;
  if (ghost.kind === "feature" && live.kind === "feature" && ghost.id !== live.id) {
    return null;
  }
  const from = {
    latitude: ghost.latitude,
    longitude: ghost.longitude
  };
  const to = {
    latitude: live.latitude,
    longitude: live.longitude
  };
  const distance = haversineDistance(from.latitude, from.longitude, to.latitude, to.longitude);
  if (distance < MOVEMENT_MIN_METERS) return null;
  return {
    from,
    to,
    distance
  };
}

// ─── Marker visuals (JSX, not DOM strings) ─────────────────────────────────

function PlaceMarkerVisual(t0) {
  const $ = _c(15);
  const {
    draggable,
    ghost
  } = t0;
  if (ghost) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        width: 32,
        height: 40,
        cursor: "default"
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = /*#__PURE__*/_jsx("div", {
        title: "Ursprunglig position",
        className: "pointer-events-none",
        style: t1,
        children: /*#__PURE__*/_jsxs("svg", {
          width: "32",
          height: "40",
          viewBox: "0 0 32 40",
          fill: "none",
          children: [/*#__PURE__*/_jsx("path", {
            d: "M16 1C7.716 1 1 7.716 1 16c0 11 15 23 15 23s15-12 15-23C31 7.716 24.284 1 16 1z",
            fill: "white",
            stroke: PLACE_COLOR,
            strokeWidth: "2",
            strokeDasharray: "4 2",
            strokeLinejoin: "round"
          }), /*#__PURE__*/_jsx("circle", {
            cx: "16",
            cy: "16",
            r: "3",
            fill: PLACE_COLOR,
            opacity: "0.6"
          })]
        })
      });
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t1 = draggable ? "Plats \u2014 dra f\xF6r att flytta" : "Plats";
  const t2 = draggable ? "grab" : "default";
  let t3;
  if ($[2] !== t2) {
    t3 = {
      width: 32,
      height: 40,
      cursor: t2,
      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))"
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = /*#__PURE__*/_jsx("path", {
      d: "M16 0C7.163 0 0 7.163 0 16c0 11 16 24 16 24s16-13 16-24C32 7.163 24.837 0 16 0z",
      fill: PLACE_COLOR,
      stroke: "white",
      strokeWidth: "2"
    });
    t5 = /*#__PURE__*/_jsx("path", {
      d: "M16 8 L10 13 V21 H22 V13 Z",
      fill: "white"
    });
    t6 = /*#__PURE__*/_jsx("rect", {
      x: "14",
      y: "16",
      width: "4",
      height: "5",
      fill: PLACE_COLOR
    });
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== draggable) {
    t7 = draggable && /*#__PURE__*/_jsx("circle", {
      cx: "16",
      cy: "16",
      r: "15",
      fill: "none",
      stroke: PLACE_COLOR,
      strokeWidth: "1.5",
      strokeDasharray: "3 3",
      opacity: "0.6"
    });
    $[7] = draggable;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t7) {
    t8 = /*#__PURE__*/_jsxs("svg", {
      width: "32",
      height: "40",
      viewBox: "0 0 32 40",
      fill: "none",
      children: [t4, t5, t6, t7]
    });
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t8) {
    t9 = /*#__PURE__*/_jsx("div", {
      title: t1,
      style: t3,
      children: t8
    });
    $[11] = t1;
    $[12] = t3;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
function FeatureMarkerVisual(t0) {
  const $ = _c(13);
  const {
    marker
  } = t0;
  const color = FEATURE_COLORS[marker.status] ?? "#94a3b8";
  if (marker.ghost) {
    const t1 = `2.5px dashed ${color}`;
    let t2;
    if ($[0] !== t1) {
      t2 = /*#__PURE__*/_jsx("div", {
        title: "Ursprunglig position",
        className: "pointer-events-none",
        style: {
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "white",
          border: t1,
          cursor: "default"
        }
      });
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t1 = marker.draggable ? `${marker.label} — dra för att flytta` : marker.label;
  const t2 = marker.unplaced ? "2.5px dashed white" : "2.5px solid white";
  const t3 = marker.draggable ? "grab" : "default";
  let t4;
  if ($[2] !== color || $[3] !== t2 || $[4] !== t3) {
    t4 = {
      position: "relative",
      width: 22,
      height: 22,
      borderRadius: "50%",
      background: color,
      border: t2,
      boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
      cursor: t3
    };
    $[2] = color;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== color || $[7] !== marker.draggable) {
    t5 = marker.draggable && /*#__PURE__*/_jsx("div", {
      className: "pointer-events-none",
      style: {
        position: "absolute",
        inset: -8,
        borderRadius: "50%",
        border: `2px dashed ${color}`,
        opacity: 0.7
      }
    });
    $[6] = color;
    $[7] = marker.draggable;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t4 || $[11] !== t5) {
    t6 = /*#__PURE__*/_jsx("div", {
      title: t1,
      style: t4,
      children: t5
    });
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}

// ─── Component ─────────────────────────────────────────────────────────────

export function PlaceFeatureMap({
  markers,
  onMove
}) {
  const styles = useMemo(() => ({
    light: CARTO_STYLE
  }), []);
  const initialCenter = useMemo(() => {
    const place = markers.find(m => m.kind === "place");
    if (place) return [place.longitude, place.latitude];
    const first = markers[0];
    if (first) return [first.longitude, first.latitude];
    return [15.985, 60.279];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/_jsxs(Map, {
    styles: styles,
    center: initialCenter,
    zoom: 18,
    minZoom: 15,
    maxZoom: 21,
    attributionControl: false,
    children: [/*#__PURE__*/_jsx(MapControls, {
      position: "top-right",
      showZoom: true
    }), /*#__PURE__*/_jsx(FitBoundsOnce, {
      markers: markers
    }), /*#__PURE__*/_jsx(MovementLine, {
      markers: markers
    }), markers.map((marker, i) => {
      const isDraggable = marker.draggable && !marker.ghost;
      const handleDragEnd = isDraggable ? ({
        lng,
        lat
      }) => {
        if (marker.kind === "place") {
          onMove?.({
            kind: "place",
            latitude: lat,
            longitude: lng
          });
        } else {
          onMove?.({
            kind: "feature",
            id: marker.id,
            latitude: lat,
            longitude: lng
          });
        }
      } : undefined;
      const key = marker.kind === "place" ? `place:${marker.ghost ? "ghost" : "live"}:${i}` : `feature:${marker.id}:${marker.ghost ? "ghost" : "live"}`;
      return /*#__PURE__*/_jsx(MapMarker, {
        longitude: marker.longitude,
        latitude: marker.latitude,
        draggable: isDraggable,
        anchor: marker.kind === "place" ? "bottom" : "center",
        onDragEnd: handleDragEnd,
        children: /*#__PURE__*/_jsx(MarkerContent, {
          children: marker.kind === "place" ? /*#__PURE__*/_jsx(PlaceMarkerVisual, {
            draggable: marker.draggable,
            ghost: marker.ghost ?? false
          }) : /*#__PURE__*/_jsx(FeatureMarkerVisual, {
            marker: marker
          })
        })
      }, key);
    })]
  });
}

/** Auto-fit bounds once on mount — drags must not re-pan the map. */
function FitBoundsOnce({
  markers
}) {
  const {
    map,
    isLoaded
  } = useMap();
  const didFitRef = useRef(false);
  useEffect(() => {
    if (!isLoaded || !map || didFitRef.current) return;
    if (markers.length > 1) {
      const bounds = new maplibregl.LngLatBounds();
      for (const m of markers) bounds.extend([m.longitude, m.latitude]);
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, {
          padding: 60,
          maxZoom: 19,
          duration: 0
        });
        didFitRef.current = true;
      }
    } else if (markers.length === 1) {
      const only = markers[0];
      map.jumpTo({
        center: [only.longitude, only.latitude]
      });
      didFitRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map]);
  return null;
}

/** Dashed line + distance pill between ghost and live draggable markers. */
function MovementLine(t0) {
  const $ = _c(5);
  const {
    markers
  } = t0;
  const {
    map,
    isLoaded
  } = useMap();
  const pillRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== isLoaded || $[1] !== map || $[2] !== markers) {
    t1 = () => {
      if (!isLoaded || !map) {
        return;
      }
      if (pillRef.current) {
        pillRef.current.remove();
        pillRef.current = null;
      }
      if (map.getLayer(MOVEMENT_LINE_LAYER)) {
        map.removeLayer(MOVEMENT_LINE_LAYER);
      }
      if (map.getSource(MOVEMENT_LINE_SOURCE)) {
        map.removeSource(MOVEMENT_LINE_SOURCE);
      }
      const pair = findMovementPair(markers);
      if (!pair) {
        return;
      }
      map.addSource(MOVEMENT_LINE_SOURCE, {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: [[pair.from.longitude, pair.from.latitude], [pair.to.longitude, pair.to.latitude]]
          }
        }
      });
      map.addLayer({
        id: MOVEMENT_LINE_LAYER,
        type: "line",
        source: MOVEMENT_LINE_SOURCE,
        layout: {
          "line-cap": "round",
          "line-join": "round"
        },
        paint: {
          "line-color": "#475569",
          "line-width": 2,
          "line-dasharray": [2, 2],
          "line-opacity": 0.85
        }
      });
      const midLng = (pair.from.longitude + pair.to.longitude) / 2;
      const midLat = (pair.from.latitude + pair.to.latitude) / 2;
      const pill = document.createElement("div");
      pill.textContent = formatDistance(pair.distance);
      pill.style.cssText = "\n      background: #0f172a;\n      color: white;\n      padding: 2px 8px;\n      border-radius: 9999px;\n      font-size: 10px;\n      font-weight: 600;\n      font-family: system-ui, sans-serif;\n      white-space: nowrap;\n      box-shadow: 0 2px 6px rgba(0,0,0,0.35);\n      pointer-events: none;\n      letter-spacing: 0.02em;\n    ";
      pillRef.current = new maplibregl.Marker({
        element: pill,
        anchor: "center"
      }).setLngLat([midLng, midLat]).addTo(map);
      return () => {
        if (pillRef.current) {
          pillRef.current.remove();
          pillRef.current = null;
        }
        try {
          if (map.getLayer(MOVEMENT_LINE_LAYER)) {
            map.removeLayer(MOVEMENT_LINE_LAYER);
          }
          if (map.getSource(MOVEMENT_LINE_SOURCE)) {
            map.removeSource(MOVEMENT_LINE_SOURCE);
          }
        } catch {}
      };
    };
    t2 = [isLoaded, map, markers];
    $[0] = isLoaded;
    $[1] = map;
    $[2] = markers;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}